/**
 * Wishlist Management Class - Backend API Version
 * Works with your shopify-birdiestep API
 */

// API Configuration - CHANGE THIS TO YOUR DEPLOYED API URL
const API_BASE_URL = "https://api.birdiestep.ro"; // TODO: Replace with your Railway/Render URL

class WishlistManager {
  constructor() {
    this.apiUrl = `${API_BASE_URL}/api/favorites`;
    this.customerId = null;
    this.cache = []; // Local cache for faster access
    this.init();
    this.createNotificationContainer();
  }

  /**
   * Initialize wishlist
   */
  async init() {
    // Get customer ID from Liquid/global variable
    this.customerId = window.shopifyCustomerId || null;

    // Load initial data if customer is logged in
    if (this.customerId) {
      await this.loadFromAPI();
    }
  }

  /**
   * Create notification container
   */
  createNotificationContainer() {
    if (!document.getElementById("wishlist-notification-container")) {
      const container = document.createElement("div");
      container.id = "wishlist-notification-container";
      container.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 10000;
        display: flex;
        flex-direction: column;
        gap: 10px;
        pointer-events: none;
      `;
      document.body.appendChild(container);
    }
  }

  /**
   * Show notification
   */
  showNotification(message, type = "success") {
    const container = document.getElementById(
      "wishlist-notification-container",
    );
    if (!container) return;

    const notification = document.createElement("div");
    notification.className = "wishlist-notification";

    const colors = {
      success: "#4caf50",
      error: "#f44336",
      info: "#2196f3",
    };

    const icons = {
      success: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" stroke-width="2">
        <path d="M16 6L8 14L4 10"></path>
      </svg>`,
      error: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" stroke-width="2">
        <path d="M4 4L16 16M16 4L4 16"></path>
      </svg>`,
      info: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" stroke-width="2">
        <circle cx="10" cy="10" r="8"></circle>
        <path d="M10 10V14M10 6V7"></path>
      </svg>`,
    };

    notification.innerHTML = `
      <div style="
        background: ${colors[type]};
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 250px;
        pointer-events: auto;
        animation: slideInRight 0.3s ease-out;
      ">
        <span style="display: flex; align-items: center; flex-shrink: 0;">
          ${icons[type]}
        </span>
        <span style="flex: 1; font-size: 14px; font-weight: 500;">
          ${message}
        </span>
      </div>
    `;

    if (!document.getElementById("wishlist-notification-styles")) {
      const style = document.createElement("style");
      style.id = "wishlist-notification-styles";
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(400px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(400px); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    container.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = "slideOutRight 0.3s ease-out";
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * Make API request
   */
  async request(endpoint, options = {}) {
    if (!this.customerId) {
      throw new Error("Customer ID not available");
    }

    const url = endpoint.startsWith("http")
      ? endpoint
      : `${this.apiUrl}${endpoint}`;

    const token = typeof window.getCustomerToken === 'function'
      ? (await window.getCustomerToken())
      : sessionStorage.getItem('customerToken');
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": window.shopifyApiKey || "",
      "x-customer-token": token || "",
      ...options.headers,
    };

    try {
      let response = await fetch(url, { ...options, headers });

      // Token expired — refresh and retry once
      if (response.status === 401) {
        const newToken = await this._refreshToken();
        if (newToken) {
          headers['x-customer-token'] = newToken;
          response = await fetch(url, { ...options, headers });
        }
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error("API error:", error);
      throw error;
    }
  }

  /**
   * Load favorites from API
   */
  async loadFromAPI() {
    if (!this.customerId) return [];

    try {
      const data = await this.request("");

      if (data.success) {
        // Map API response to cache format
        this.cache = data.favorites.map((item) => ({
          id: item.product_id,
          handle: "", // Not provided by API, will be set when adding
          img: item.product_image_url,
          title: item.product_title,
          price: item.product_price,
          variant_id: item.variant_id,
          addedAt: item.created_at,
        }));

        return this.cache;
      }

      return [];
    } catch (error) {
      console.error("Error loading favorites:", error);
      return [];
    }
  }

  /**
   * Add product to wishlist
   */
  async add(id, handle, img, title, price, variantId = null) {
    // Check authentication
    if (!this.customerId) {
      const loginUrl =
        typeof customerLoginUrl !== "undefined"
          ? customerLoginUrl
          : "/account/login";
      window.location.href = `${loginUrl}?return_url=${encodeURIComponent(window.location.pathname)}`;
      return;
    }

    try {
      // Check if already exists
      if (this.has(id)) {
        console.log("Product already in wishlist");
        return;
      }

      // Add to API
      const response = await this.request("", {
        method: "POST",
        body: JSON.stringify({
          product_id: id,
          variant_id: variantId,
          product_title: title,
          product_image_url: img,
          product_price: price,
        }),
      });

      if (response.success) {
        // Update cache
        this.cache.unshift({
          id: id,
          handle: handle,
          img: img,
          title: title,
          price: price,
          variant_id: variantId,
          addedAt: new Date().toISOString(),
        });

        // Dispatch event
        document.dispatchEvent(
          new CustomEvent("wishlist:updated", {
            detail: { action: "added", productId: id },
          }),
        );

        this.showNotification("Produs adăugat la favorite", "success");
        console.log("Product added to wishlist:", title);
      }
    } catch (error) {
      console.error("Error adding to wishlist:", error);
      this.showNotification("Eroare la adăugare în favorite", "error");
    }
  }

  /**
   * Get all wishlist items
   */
  get() {
    return this.cache;
  }

  /**
   * Remove product from wishlist
   */
  async remove(id) {
    if (!this.customerId) return;

    try {
      // Remove from API
      const response = await this.request(`/${id}`, {
        method: "DELETE",
      });

      if (response.success) {
        // Update cache
        this.cache = this.cache.filter((item) => item.id !== id);

        // Dispatch event
        document.dispatchEvent(
          new CustomEvent("wishlist:updated", {
            detail: { action: "removed", productId: id },
          }),
        );

        this.showNotification("Produs eliminat de la favorite", "info");
        console.log("Product removed from wishlist:", id);
      }
    } catch (error) {
      console.error("Error removing from wishlist:", error);
      this.showNotification("Eroare la eliminare din favorite", "error");
    }
  }

  /**
   * Check if product is in wishlist
   */
  has(id) {
    return this.cache.some(
      (item) => item.id === id.toString() || item.id === parseInt(id),
    );
  }

  /**
   * Get wishlist item count
   */
  count() {
    return this.cache.length;
  }

  /**
   * Clear entire wishlist (not implemented in API, would need to remove one by one)
   */
  async clear() {
    if (!this.customerId) return;

    try {
      const items = [...this.cache];

      for (const item of items) {
        await this.remove(item.id);
      }

      this.showNotification("Toate favoritele au fost șterse", "info");
    } catch (error) {
      console.error("Error clearing wishlist:", error);
      this.showNotification("Eroare la ștergere favorite", "error");
    }
  }

  /**
   * Refresh the customer token from the API
   */
  async _refreshToken() {
    if (!this.customerId) return null;
    try {
      const res = await fetch('https://api.birdiestep.ro/auth/customer-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': window.shopifyApiKey || '' },
        body: JSON.stringify({ customerId: this.customerId.toString() }),
      });
      const d = await res.json();
      if (d.token) {
        sessionStorage.setItem('customerToken', d.token);
        sessionStorage.setItem('customerTokenExp', String(d.expiresAt));
        return d.token;
      }
    } catch (e) {}
    return null;
  }

  /**
   * Migrate data from localStorage to API
   */
  async migrateFromLocalStorage() {
    if (!this.customerId) return;

    const localStorageKey = "yv-wishlist";
    const localData = localStorage.getItem(localStorageKey);

    if (!localData) {
      console.log("No localStorage data to migrate");
      return;
    }

    try {
      const items = JSON.parse(localData);
      console.log(
        `Migrating ${items.length} items from localStorage to API...`,
      );

      let successCount = 0;
      for (const item of items) {
        try {
          await this.add(
            item.id,
            item.handle,
            item.img,
            item.title,
            item.price,
          );
          successCount++;
        } catch (error) {
          console.error(`Failed to migrate item ${item.id}:`, error);
        }
      }

      // Clear localStorage after successful migration
      localStorage.removeItem(localStorageKey);
      console.log(
        `Migration complete! ${successCount}/${items.length} items migrated.`,
      );
      this.showNotification(
        `${successCount} favorite sincronizate!`,
        "success",
      );
    } catch (error) {
      console.error("Error migrating data:", error);
      this.showNotification("Eroare la sincronizare", "error");
    }
  }
}

// Initialize global Wishlist instance
window.Wishlist = new WishlistManager();

// Auto-migrate from localStorage on first load
if (window.shopifyCustomerId && localStorage.getItem("yv-wishlist")) {
  window.Wishlist.migrateFromLocalStorage();
}

// Toggle wishlist function for product grid buttons
window.toggleWishlist = async function (button) {
  const productId = button.getAttribute("data-product-id");
  const productHandle = button.getAttribute("data-product-handle");
  const productImg = button.getAttribute("data-product-img");
  const productTitle = button.getAttribute("data-product-title");
  const productPrice = button.getAttribute("data-product-price");
  const variantId = button.getAttribute("data-variant-id");

  if (Wishlist.has(productId)) {
    await Wishlist.remove(productId);
  } else {
    await Wishlist.add(
      productId,
      productHandle,
      productImg,
      productTitle,
      productPrice,
      variantId,
    );
  }
};

// Update tooltip text
window.updateTooltipText = function (button) {
  const productId = button.getAttribute("data-product-id");
  const tooltip = button.querySelector(".tooltip-custom");

  if (!tooltip) return;

  const addText = tooltip.getAttribute("data-add-text") || "Adaugă la favorite";
  const removeText =
    tooltip.getAttribute("data-remove-text") || "Elimină de la favorite";

  if (Wishlist.has(productId)) {
    tooltip.textContent = removeText;
    button.setAttribute("aria-label", removeText);
  } else {
    tooltip.textContent = addText;
    button.setAttribute("aria-label", addText);
  }
};

// Listen for wishlist updates
document.addEventListener("wishlist:updated", function (event) {
  const { action, productId } = event.detail;

  if (action === "added" || action === "removed") {
    const buttons = document.querySelectorAll(
      `.wishlist-btn-custom[data-product-id="${productId}"]`,
    );
    buttons.forEach((button) => {
      if (action === "added") {
        button.classList.add("in-wishlist");
      } else {
        button.classList.remove("in-wishlist");
      }
      updateTooltipText(button);
    });
  } else if (action === "cleared") {
    const buttons = document.querySelectorAll(
      ".wishlist-btn-custom[data-product-id]",
    );
    buttons.forEach((button) => {
      button.classList.remove("in-wishlist");
      updateTooltipText(button);
    });
  }
});

// Update button states on page load
document.addEventListener("DOMContentLoaded", async function () {
  // Wait for wishlist to load from API
  if (window.shopifyCustomerId) {
    await Wishlist.loadFromAPI();
  }

  const buttons = document.querySelectorAll(
    ".wishlist-btn-custom[data-product-id]",
  );
  buttons.forEach((button) => {
    const productId = button.getAttribute("data-product-id");

    if (Wishlist.has(productId)) {
      button.classList.add("in-wishlist");
    }

    updateTooltipText(button);

    button.addEventListener("mouseenter", function () {
      updateTooltipText(this);
    });
  });
});
